import { GetRowIdParams, RowSelectedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useEffect, useRef, useState } from 'react';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../components/Button/Button';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { useAddRawProductOverrides, useRawProdSourceById } from '../../../services/rawProd/rawProd.service';
import { useStoreMarketsOrganizations } from '../../../services/store/store.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { ButtonContainer, PageContainer, StyledTableWrapper } from './AddRawProductOverrides.styles';
import { AddRawProductOverridesProps } from './AddRawProductOverrides.types';
import Confirmation from './Confirmation/Confirmation';
import {
  StyledFooter,
  StyledFooterText,
  StyledHeader,
  StyledHeaderTitle,
} from '../../../components/Table/Table.styles';
import { StyledSubHeader, StyledSubTitle } from '../../Extract/Menu/SelectMenus/SelectMenus.styles';
import MenuTreeView from '../../../components/MenuTreeView/MenuTreeView';
import {
  BuildRequestBodyParams,
  IndexableDataStructureWrapper,
} from '../../../components/MenuTreeView/MenuTreeView.types';
import { Organization } from '../../../services/menu/menu.types';
import useTreeViewRowStates from '../../../components/MenuTreeView/useMenuTreeViewRowStates';
import isEqual from 'react-fast-compare';
import Checkbox from '../../../components/Checkbox/Checkbox';

const AddRawProductOverrides: FC<AddRawProductOverridesProps> = ({ selectedRawProdId }) => {
  const selectedGridRef = useRef<AgGridReact>(null);
  const [selectedStores, setSelectedStores] = useState<Organization[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const { data: organizations, isError: organizationsError, isLoading } = useStoreMarketsOrganizations(true);
  const { data: rawProdSourceByIdData, isError: rawProdSourceByIdError } = useRawProdSourceById(selectedRawProdId);
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const [tableUpdate, setTableUpdate] = useState<boolean>(false);

  const { mutate, data: addRawProdOverridesResponse } = useAddRawProductOverrides(selectedRawProdId);

  useEffect(() => {
    let count = 0;
    if (organizations?.data !== undefined && organizations.data.length > 0) {
      organizations.data.forEach((org) => {
        if (org.children !== undefined) {
          org.children.forEach((store) => {
            count++;
          });
        }
      });
    }
    setDisplayedRowCount(count);
  }, [organizations]);

  useFetchErrorToast([
    { isError: organizationsError, label: 'Error fetching organizations.', id: 'organizationStoresFetchError' },
    { isError: rawProdSourceByIdError, label: 'Error fetching raw product.', id: 'rawProdSourceByIdFetchError' },
  ]);

  const dataStructure: IndexableDataStructureWrapper = {
    "org": {
      idKey: 'org',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        return [{ level: 0, id: params.id }];
      },
      getIndexByType: () => { return 0}
    },
    "store": {
      to: () => '',
      idKey: 'store',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.label,
      buildParentArray: (params: Organization) => {
        const parents = [];
        if (params.parents) {
          parents.push({level: 0, id: params.parents['org']});
        }
        parents.push({level: 1, id: params.id});
        return parents;
      },
      getIndexByType: () => { return 1}
    }
  }

  const { checkedRows, expandedRows, handleRowClick, handleCheckboxClick, handleUpwardsDeselectPropagation, handleExternalTableDeselectPropagation, resetCheckedRows } = useTreeViewRowStates(
    dataStructure,
    'storeOverrides' + rawProdSourceByIdData?.data.moaRawProdNo,
    true
  );

  const handleSelectedMenusChange = (event: RowSelectedEvent) => {
    if (event.node.isSelected() === false && event.rowIndex !== null) {
      const newSelectedMenus = selectedStores.filter((store) => store.id !== event.data.id);
      setSelectedStores(newSelectedMenus);
      handleExternalTableDeselectPropagation(event.data);
      handleCheckboxClick(event.data, false);
      selectedGridRef.current?.api.selectAll();

      setTableUpdate(!tableUpdate);
    }
  };

  useEffect(() => {
    selectedGridRef?.current?.api?.selectAll();
  }, [selectedStores, selectedGridRef?.current?.api]);

  const handleConfirmClick = () => {
    setShowConfirmation(true);
    const stores = selectedStores.map(row => row.id);
    mutate(stores, {
      onSuccess: () => {
        // Render confirmation view on success
        setShowConfirmation(true);
        showToast({
          label: 'Overrides have been submitted.',
          id: 'addRawProductOverridesSuccess',
          toastType: ToastTypes.Success,
        });
      },
    });
  };

  const handleCheckboxClickInternal = (row: Organization, rowChecked: boolean) => {
    if (rowChecked) {
      if (row.type === "store") {
        // menu row was checked
        setSelectedStores((selectedStore) => {
          const selectedStoresWithCheckedRowAdded = [...selectedStore, row];
          return Array.from(new Set(selectedStoresWithCheckedRowAdded));
        });
      } else {
          traverseTree(row);
      }
    } else {
      // row was just unchecked
      setSelectedStores((selectedStores) => {
        // filter through selectedMenus and remove all the menus that correspond with that row
        return selectedStores.filter((store) => {
          let isRowAndParentEqual;
          if (store.type === row.type) {
            isRowAndParentEqual = isEqual(row.id, store.id) && isEqual(row.parents, store.parents);
          } else {
            isRowAndParentEqual = isEqual(row.id, store.parents ? store.parents[row.type] : undefined);
          }
          return !isRowAndParentEqual;
        });
      });
      handleUpwardsDeselectPropagation(row);
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row, rowChecked);
  };

  const traverseTree = (row: Organization) => {
    if (row.type === 'store') {
      setSelectedStores((selectedStore) => {
        const selectedStoresWithCheckedRowAdded = [...selectedStore, row];
        return Array.from(new Set(selectedStoresWithCheckedRowAdded));
      });
      handleCheckboxClick(row, true);
    } else if (row.children !== undefined) {
      row.children.forEach(childRow => traverseTree(childRow))
    }
  }

  const handleSelectAll = () => {
    if (selectedStores.length === displayedRowCount && !isLoading) {
      resetCheckedRows();
      setSelectedStores([]);
    } else if (organizations?.data !== undefined) {
      let allStores: Organization[] = [];
      organizations.data.forEach((org) => {
        handleCheckboxClick(org, true);
        if (org.children !== undefined) {
          org.children.forEach((store) => {
            allStores.push(store);
            handleCheckboxClick(store, true);
          });
        }
      });
      setSelectedStores(allStores);
    }
  };

  const getRowId = (params: GetRowIdParams) => {
    return params.data.id;
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to All Raw Products', to: '/tables/rawproducts' }}
        title={
          rawProdSourceByIdData?.data
            ? `Add Store Number Overrides Raw Product - ${rawProdSourceByIdData?.data?.moaRawProdNo} - ${rawProdSourceByIdData?.data?.mxRef}`
            : 'Add Store Number Overrides Raw Product'
        }
      />
      {!showConfirmation ? (
        <PageContainer>
          <H2>Select store numbers for override</H2>
          <TableRowWrapper>
            <StyledTableWrapper>
              <StyledHeader data-testid='header'>
                <StyledHeaderTitle>Available Store Numbers</StyledHeaderTitle>
              </StyledHeader>
              <StyledSubHeader data-testid='subtitle'>
                <div style={{ marginLeft: '48px' }}>
                  <Checkbox
                    type='checkbox'
                    checked={selectedStores.length > 0 && selectedStores.length === displayedRowCount}
                    id={'select-all'}
                    aria-checked={selectedStores.length > 0 && selectedStores.length === displayedRowCount}
                    aria-disabled={'false'}
                    readOnly
                    indeterminate={selectedStores.length > 0 && selectedStores.length < displayedRowCount}
                    label={<StyledSubTitle>Organizations</StyledSubTitle>}
                    onClick={handleSelectAll}
                  />
                </div>
              </StyledSubHeader>
              <MenuTreeView
                rows={organizations?.data || []}
                dataStructure={dataStructure}
                checkedRows={checkedRows}
                expandOnCheck={false}
                expandedRows={expandedRows}
                onRowClick={handleRowClick}
                onCheckboxClick={handleCheckboxClickInternal}
                defaultHandleCheckboxClick={handleCheckboxClick}
                isLoading={isLoading}
                selectedRows={selectedStores}
                externalTableUpdate={tableUpdate}
              />
              <StyledFooter>
                <StyledFooterText>
                  {`${displayedRowCount} ${displayedRowCount === 1 ? 'record' : 'records'}`}
                </StyledFooterText>
              </StyledFooter>
            </StyledTableWrapper>
            <StyledTableWrapper>
              <Table
                ref={selectedGridRef}
                showHeader={true}
                headerTitle='Selected Store Numbers'
                columnDefs={[
                  {
                    minWidth: 70,
                    maxWidth: 70,
                    flex: 1,
                    headerName: 'Select',
                    checkboxSelection: true,
                    floatingFilter: false,
                    sortable: false,
                  },
                  { minWidth: 60, flex: 1, field: 'id', headerName: 'Store No.', resizable: false },
                  { minWidth: 60, flex: 1, field: 'additionalProperties.marketNo', headerName: 'Market No.', resizable: false },
                ]}
                rowData={selectedStores}
                rowSelection='multiple'
                suppressRowClickSelection={true}
                onRowSelected={handleSelectedMenusChange}
                returnedRecords={selectedStores.length || 0}
                height={'100%'}
                getRowId={getRowId}
                animateRows={false}
              />
            </StyledTableWrapper>
          </TableRowWrapper>
          <ButtonContainer>
            <Button label='Confirm' onClick={handleConfirmClick} disabled={selectedStores.length === 0} />
          </ButtonContainer>
        </PageContainer>
      ) : (
        <Confirmation rowData={addRawProdOverridesResponse?.data} />
      )}
    </>
  );
};

export default AddRawProductOverrides;
